import { nanoid } from "@reduxjs/toolkit";
import {
  Button,
  Collapse,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Result,
  Select,
  Space,
  Switch,
  Tag,
  TagProps,
  Tooltip,
  Typography,
  Modal,
  Dropdown,
  notification,
  Card,
} from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { loader, SingleQueryWrapper } from "components/QueryWrapper";
import dayjs from "dayjs";
import { CommunicationSchemaDto, Conclusion, MessageType, NegotiationDto } from "dtos/communication.dto";
import { GeneratedEmailDto, NegotiationChatDto } from "dtos/negotiation.chat.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useRef, useState } from "react";
import {
  TbChevronLeft,
  TbChevronRight,
  TbClipboardCheck,
  TbCopy,
  TbLoader2,
  TbPlus,
  TbRefresh,
  TbX,
  TbCheck,
  TbTrash,
  TbEdit,
  TbSend,
  TbInbox,
  TbClipboard,
} from "react-icons/tb";
import { useParams, useSearchParams } from "react-router-dom";
import { grayColours } from "utils/constants";
import { toTitleCase } from "utils/functions";
import { useQueryClient } from "@tanstack/react-query";
import { T_Api_Success_Res } from "api/api.types";

const prepareForSendEmail = ({
  appKey,
  ncId,
  pncId,
  loanId,
  sendAsOrg,
}: {
  appKey?: string;
  ncId: string;
  pncId?: string;
  loanId?: string;
  sendAsOrg: boolean;
}) => {
  const searchParams = new URLSearchParams();
  searchParams.set("ncId", ncId);
  if (pncId) searchParams.set("pncId", pncId);
  if (loanId) searchParams.set("loanId", loanId);
  searchParams.set("sendAsOrg", String(sendAsOrg));

  const url = `/dashboard/application/${appKey}/email?${searchParams.toString()}`;

  window.open(url, "_blank");
};

const redirectToMailInbox = ({
  appKey,
  messageId,
  threadId,
}: {
  appKey?: string;
  threadId?: string;
  messageId?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (threadId) searchParams.set("threadId", threadId);
  if (messageId) searchParams.set("messageId", messageId);

  const url = `/dashboard/application/${appKey}/email?${searchParams.toString()}`;
  window.open(url, "_blank");
};

export default function Negotiation() {
  const { appKey, loanId } = useParams();
  const [isGenerating, setIsGenerating] = useState(false);
  const [followupText, setFollowupText] = useState("");
  const queryClient = useQueryClient();

  const [regenerateTimestamp, setRegenerateTimestamp] = useState<number | null>(null);
  const [chatSummary, setChatSummary] = useState<Conclusion | undefined>(undefined);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  const [messageVersionLengths, setMessageVersionLengths] = useState<Record<string, number>>({});
  const fullApplication = ApiService.application.useGetByAppKey(appKey);

  const communicationConfig = ApiService.communication.useGetCommunicationConfigByCreditorId(loanId);
  const communicationEmails = ApiService.communication.useGetCommunicationChatsByCreditorId(
    loanId,
    isGenerating || !!regeneratingMessageId,
  );

  const initiateNegotiation = ApiService.communication.useInitiateNegotiation();
  const followupNegotiation = ApiService.communication.useFollowUpNegotiation();
  const createOrUpdateConfig = ApiService.communication.usePostCommunication();
  const regenerateNegotiation = ApiService.communication.useReGenerateNegotiation();
  const regenerateMailTonality = ApiService.communication.useRegenerateNegotiationTonality();
  const tonalityList = ApiService.communication.useGetTonalityList();
  const generateConclusionRes = ApiService.communication.useGenerateConclusion();

  const [form] = Form.useForm<NegotiationDto>();
  const [emailLen, setEmailLen] = useState(communicationEmails?.data?.result?.length);
  const clearChat = ApiService.communication.useDeleteChat();
  const firstMailInitiatedIndex =
    communicationEmails.data?.result.findIndex((c) => c.mail_message_id || c.versions.some((v) => v.mail_message_id)) ??
    -1;

  const lastMsgRef = useRef<HTMLDivElement>(null);
  // console.log({ summary: communicationConfig.data?.result?.conclusion?.probable_conclusion });

  // Track version lengths for each message
  useEffect(() => {
    if (communicationEmails?.data?.result) {
      const newVersionLengths: Record<string, number> = {};
      communicationEmails.data.result.forEach((msg) => {
        newVersionLengths[msg._id] = msg.versions?.length || 0;
      });
      setMessageVersionLengths(newVersionLengths);
    }
  }, [communicationEmails?.data?.result]);

  useEffect(() => {
    if (isGenerating && communicationEmails?.data?.result) {
      if (regenerateTimestamp !== null) {
        const lastMessage = communicationEmails?.data?.result?.[communicationEmails?.data?.result?.length - 1];
        if (lastMessage && new Date(lastMessage.timestamp).getTime() > regenerateTimestamp) {
          console.log("regenerate complete");
          setIsGenerating(false);
          setRegenerateTimestamp(null);
          lastMsgRef.current?.scrollIntoView();
        }
      } else if (communicationEmails?.data?.result?.length !== emailLen) {
        console.log("generating");
        setIsGenerating(false);
        lastMsgRef.current?.scrollIntoView();
      }
    }
  }, [isGenerating, communicationEmails?.data?.result, emailLen, regenerateTimestamp]);

  return (
    <DashboardLayout
      headerRight={
        <div className="flex items-center gap-4">
          {communicationEmails.data?.result?.length !== 0 &&
            ((communicationConfig as unknown as T_Api_Success_Res<Omit<CommunicationSchemaDto, "emails">>)?.result
              ?.conclusion?.probable_conclusion !== "" ? (
              <Button
                onClick={() => {
                  setChatSummary((communicationConfig.data?.result as any)?.conclusion);
                  setIsSummaryModalOpen(true);
                }}
                type="primary"
              >
                View Summary
              </Button>
            ) : (
              <Tooltip title="Generate conclusion of this chat">
                <Button
                  onClick={() => {
                    generateConclusionRes.mutate(
                      { app_key: appKey, creditor_id: loanId },
                      {
                        onSuccess: () => {
                          notification.info({
                            message: "Conclusion generation in progress",
                            description: "Please wait while we generate the conclusion. This may take up to a minute.",
                          });
                        },
                      },
                    );
                  }}
                  type="primary"
                >
                  Generate Summary
                </Button>
              </Tooltip>
            ))}
          <Button
            disabled={communicationEmails.data?.result?.length === 0}
            onClick={() => {
              // clearChat.mutate(
              //   { chatId: communicationConfig.data?.result._id as string },
              //   {
              //     onSuccess: () => {
              //       message.success("Chat cleared sucessfully");
              //       queryClient.invalidateQueries({ queryKey: ["coomunication", "chats"] });
              //     },
              //   },
              // );
              Modal.confirm({
                title: "Are you sure you want to clear the chat history? This action cannot be undone.",
                onOk: () => {
                  clearChat.mutate(
                    { chatId: communicationConfig.data?.result._id as string },
                    {
                      onSuccess: () => {
                        message.success("Chat cleared sucessfully");
                        queryClient.invalidateQueries({ queryKey: ["coomunication", "chats"] });
                      },
                    },
                  );
                },
              });
            }}
            icon={<TbRefresh />}
          >
            Clear Chat
          </Button>
        </div>
      }
    >
      <PageTitle
        title="Negotiation"
        pageTitle={[
          { title: `Applications`, link: "/dashboard/applications" },
          {
            title: fullApplication?.data?.data?.application?.client_details?.name ?? "Application Item",

            link: `/dashboard/application/${appKey}`,
          },
          {
            title: "Calculator",
            link: `/dashboard/application/${appKey}/calculator`,
          },
          { title: "Negotiation" },
        ]}
      />
      <SingleQueryWrapper
        query={communicationConfig}
        renderData={(commConfig) => {
          return (
            <div className="grid grid-cols-[1fr_350px]">
              <div>
                {!communicationEmails?.data?.result || communicationEmails?.data?.result?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center">
                    <Result
                      className="w-fit"
                      icon={Empty.PRESENTED_IMAGE_DEFAULT}
                      status={"error"}
                      // title={query.error.response?.statusText}
                      subTitle={"Negotiation not started"}
                      extra={
                        isGenerating ? null : (
                          <div className="flex flex-col space-y-4">
                            <Button
                              type="primary"
                              loading={createOrUpdateConfig.isPending || initiateNegotiation.isPending}
                              onClick={() => {
                                const config = form.getFieldsValue(true) as NegotiationDto;
                                if (appKey && loanId) {
                                  createOrUpdateConfig.mutate(
                                    { ...config, creditor_id: loanId },
                                    {
                                      onSuccess: () => {
                                        initiateNegotiation.mutate(
                                          { app_key: appKey, creditor_id: loanId },
                                          {
                                            onSuccess: () => {
                                              setEmailLen(communicationEmails?.data?.result?.length ?? 0);
                                              setIsGenerating(true);
                                            },
                                          },
                                        );
                                      },
                                    },
                                  );
                                } else {
                                  message.error("App key or loan id not found");
                                }
                              }}
                            >
                              Generate Message
                            </Button>
                          </div>
                        )
                      }
                    />
                    <div className="flex-1 flex items-center justify-center p-2">
                      {isGenerating ? (
                        <div className="flex flex-col items-center">
                          {loader}
                          <Typography.Text>
                            Please wait while we generate your message. This may take up to a minute.
                          </Typography.Text>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="relative h-[calc(100vh-50px)]">
                    <div className="flex flex-col gap-6 p-6 overflow-auto h-[calc(100vh-(50px+68px))]">
                      {communicationEmails.data?.result?.map((el, index) => {
                        const previousVersionLength = messageVersionLengths[el._id] || 0;
                        const isRegenerating = regeneratingMessageId === el._id;

                        return (
                          <MessageItem
                            key={el.timestamp}
                            message={el}
                            isRegenerating={isRegenerating}
                            previousVersionLength={previousVersionLength}
                            onRegenerateStart={(messageId: string) => {
                              setRegeneratingMessageId(messageId);
                            }}
                            onRegenerateComplete={() => {
                              setRegeneratingMessageId(null);
                            }}
                            tonalityList={tonalityList}
                            form={form}
                            appKey={appKey}
                            loanId={loanId}
                            createOrUpdateConfig={createOrUpdateConfig}
                            regenerateNegotiation={regenerateNegotiation}
                            communicationEmails={communicationEmails}
                            setFollowupText={setFollowupText}
                            setEmailLen={setEmailLen}
                            setIsGenerating={setIsGenerating}
                            lastMsgRef={lastMsgRef}
                            regenerateMailTonality={regenerateMailTonality}
                            disableMailLink={index <= firstMailInitiatedIndex}
                          />
                        );
                      })}
                      <div className="flex-1 flex items-center justify-center p-2" ref={lastMsgRef}>
                        {isGenerating ? loader : null}
                      </div>
                    </div>
                    <div className="flex items-end gap-3 p-4 border-0 border-t border-solid border-gray-200 absolute bottom-0 left-0 right-0 h-[68px] ">
                      <Input.TextArea
                        placeholder="Write creditor response here"
                        value={followupText}
                        onChange={(e) => {
                          setFollowupText(e.target.value);
                        }}
                        autoSize={{ minRows: 1, maxRows: 5 }}
                        disabled={createOrUpdateConfig.isPending || followupNegotiation.isPending}
                      />
                      <Button
                        type="primary"
                        loading={
                          createOrUpdateConfig.isPending ||
                          followupNegotiation.isPending ||
                          isGenerating ||
                          !!regeneratingMessageId
                        }
                        onClick={async () => {
                          if (followupText?.trim()?.length <= 0) {
                            return message.error("Enter creditor response to continue follow up");
                          }
                          try {
                            await form.validateFields();
                            const config = form.getFieldsValue(true) as NegotiationDto;
                            if (appKey && loanId) {
                              createOrUpdateConfig.mutate(
                                { ...config, creditor_id: loanId },
                                {
                                  onSuccess: () => {
                                    followupNegotiation.mutate(
                                      { app_key: appKey, creditor_id: loanId, creditor_response: followupText?.trim() },
                                      {
                                        onSuccess: () => {
                                          return communicationEmails.refetch().then(() => {
                                            setFollowupText("");
                                            setEmailLen((communicationEmails?.data?.result?.length ?? 0) + 1);
                                            setIsGenerating(true);
                                            lastMsgRef.current?.scrollIntoView();
                                          });
                                        },
                                      },
                                    );
                                  },
                                },
                              );
                            } else {
                              message.error("App key or loan id not found");
                            }
                          } catch (error) {
                            return message.error(
                              <div className="flex flex-col gap-0.5">
                                {(error as any)?.errorFields?.map((el: any, i: number) => {
                                  return (
                                    <Typography.Text key={i} className="!block !text-left">
                                      {el?.errors?.join("\n")}
                                    </Typography.Text>
                                  );
                                })}
                              </div>,
                            );
                          }
                        }}
                      >
                        Follow up
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <NegotiationConfig
                form={form}
                negotiationConfig={{
                  config: commConfig?.result?.config,
                  context: commConfig?.result?.context,
                }}
                appKey={appKey!}
                loanId={loanId!}
              />
            </div>
          );
        }}
      />
      <ConclusionModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        conclusion={chatSummary}
        appKey={appKey}
        loanId={loanId}
      />
    </DashboardLayout>
  );
}

function MessageItem({
  message: el,
  isRegenerating,
  onRegenerateStart,
  onRegenerateComplete,
  tonalityList,
  form,
  appKey,
  loanId,
  createOrUpdateConfig,
  regenerateNegotiation,
  communicationEmails,
  regenerateMailTonality,
  disableMailLink,
}: Readonly<{
  message: Pick<CommunicationSchemaDto, "emails">["emails"][number] & {
    parent_reply_id?: string;
    reply_id?: string;
    mail_thread_id?: string;
    mail_message_id?: string;
    type: MessageType;
    versions: NegotiationChatDto[];
  };
  disableMailLink: boolean;
  isRegenerating: boolean;
  previousVersionLength?: number;
  onRegenerateStart: (messageId: string) => void;
  onRegenerateComplete: () => void;
  tonalityList: any;
  form: any;
  appKey: string | undefined;
  loanId: string | undefined;
  createOrUpdateConfig: any;
  regenerateNegotiation: any;
  communicationEmails: any;
  regenerateMailTonality: any;

  setFollowupText?: (text: string) => void;
  setEmailLen?: (len: number) => void;
  setIsGenerating?: (generating: boolean) => void;
  lastMsgRef?: React.RefObject<HTMLDivElement>;
}>) {
  const [RegenerateMessageModalOpen, setRegenerateMessageModalOpen] = useState(false);
  const [localVersionLength, setLocalVersionLength] = useState(el.versions?.length || 0);

  const [isEditing, setIsEditing] = useState(false);
  const [isPrimaryEditing, setIsPrimaryEditing] = useState(false);
  const [editedPrimarySubject, setEditedPrimarySubject] = useState(el.subject ?? "");
  const [editedPrimaryBody, setEditedPrimaryBody] = useState(el.body ?? "");

  const [editedVersionSubject, setEditedVersionSubject] = useState("");
  const [editedVersionBody, setEditedVersionBody] = useState("");
  const [editedVersionId, setEditedVersionId] = useState<string | null>(null);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);

  const sabveEditForm = ApiService.communication.useSaveNegotation();

  const handlePrimaryEditSave = () => {
    if (!appKey || !loanId || !el._id) return;

    sabveEditForm.mutate(
      { id: el._id, subject: editedPrimarySubject, body: editedPrimaryBody },
      {
        onSuccess: () => {
          communicationEmails.refetch();
          setIsPrimaryEditing(false);
        },
        onError: () => {
          message.error("Failed to save changes. Please try again.");
        },
      },
    );
  };

  useEffect(() => {
    if (isRegenerating && el.versions) {
      const currentVersionLength = el.versions.length;
      if (currentVersionLength > localVersionLength) {
        setLocalVersionLength(currentVersionLength);
        onRegenerateComplete();
      }
    }
  }, [isRegenerating, el.versions, localVersionLength, el._id, onRegenerateComplete]);

  const handleEditCancel = () => {
    setIsEditing(false);
    setIsPrimaryEditing(false);
    setEditedVersionId(null);
  };

  const handleEditSave = ({ subject, body, versionId }: { subject: string; body: string; versionId: string }) => {
    if (!appKey || !loanId || !versionId) return;

    sabveEditForm.mutate(
      { id: versionId, subject, body },
      {
        onSuccess: () => {
          communicationEmails.refetch();
          setIsEditing(false);
        },
        onError: () => {
          message.error("Failed to save changes. Please try again.");
        },
      },
    );
  };

  // const handleEditRegenerate = ({ subject, body }: { subject: string; body: string }) => {
  //   if (!appKey || !loanId) return;

  //   onRegenerateStart(el._id);
  //   setLocalVersionLength(el.versions?.length || 0);
  //   setIsEditing(false);

  //   const config = form.getFieldsValue(true) as NegotiationDto;
  //   const userInput = subject ? `Subject: ${subject}\n\n${body}` : body;

  //   createOrUpdateConfig.mutate(
  //     { ...config, creditor_id: loanId },
  //     {
  //       onSuccess: () => {
  //         regenerateNegotiation.mutate(
  //           {
  //             app_key: appKey,
  //             creditor_id: loanId,
  //             mail: el?.body,
  //             user_input: userInput,
  //             _id: el?._id,
  //           },
  //           {
  //             onSuccess: () => {
  //               communicationEmails.refetch();
  //             },
  //           },
  //         );
  //       },
  //     },
  //   );
  // };

  const handleTonalityRegenerate = async (tone: string) => {
    if (!appKey || !loanId) return;

    try {
      await form.validateFields();
      const config = form.getFieldsValue(true) as NegotiationDto;
      const toSendConfig: NegotiationDto = {
        ...config,
        config: {
          ...config.config,
          tonality: tonalityList?.data?.result?.[tone],
        },
      };

      onRegenerateStart(el._id);
      setLocalVersionLength(el.versions?.length || 0);

      createOrUpdateConfig.mutate(
        { ...toSendConfig, creditor_id: loanId },
        {
          onSuccess: () => {
            regenerateMailTonality.mutate(
              {
                app_key: appKey,
                creditor_id: loanId,
                mail: el?.body,
                _id: el?._id,
              },
              {
                onSuccess: () => {
                  communicationEmails.refetch();
                },
              },
            );
          },
        },
      );
    } catch (error) {
      onRegenerateComplete();
      return message.error(
        <div className="flex flex-col gap-0.5">
          {(error as any)?.errorFields?.map((field: any, i: number) => (
            <Typography.Text key={i} className="!block !text-left">
              {field?.errors?.join("\n")}
            </Typography.Text>
          ))}
        </div>,
      );
    }
  };

  const handleCustomRegenerate = (inputValue: string) => {
    if (!appKey || !loanId) return;

    onRegenerateStart(el._id);
    setLocalVersionLength(el.versions?.length || 0);
    setRegenerateMessageModalOpen(false);
    const config = form.getFieldsValue(true) as NegotiationDto;

    createOrUpdateConfig.mutate(
      { ...config, creditor_id: loanId },
      {
        onSuccess: () => {
          regenerateNegotiation.mutate(
            {
              app_key: appKey,
              creditor_id: loanId,
              mail: el?.body,
              user_input: inputValue,
              _id: el?._id,
            },
            {
              onSuccess: () => {
                communicationEmails.refetch();
              },
            },
          );
        },
      },
    );
  };

  const isGeneratedMessage = el.type === MessageType.GENERATED_RESPONSE;
  const hasVersions = (el.versions ?? []).length > 0 && !!el.versions;

  return (
    <div
      className="p-2 bg-violet-50 rounded-lg max-w-[80%] border border-solid border-violet-200 relative"
      style={{
        marginLeft: el.type === MessageType.CREDITOR_RESPONSE ? "auto" : "",
        background: el.type === MessageType.CREDITOR_RESPONSE ? "white" : "",
        borderColor: el.type === MessageType.CREDITOR_RESPONSE ? grayColours[200] : "",
        paddingBottom: el.type === MessageType.CREDITOR_RESPONSE ? "" : 32,
      }}
    >
      {el.subject ? (
        <>
          <div className="flex justify-between items-center">
            <Typography.Title
              style={{ margin: 0 }}
              level={5}
              // copyable={{
              //   text: el.subject,
              //   icon: [
              //     <TbCopy key="copy-icon" className="flex-shrink-0" />,
              //     <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
              //   ],
              //   tooltips: ["Copy", "Copied"],
              // }}
            >
              <strong>Subject: {el.subject}</strong>
            </Typography.Title>{" "}
            {!disableMailLink || el.mail_thread_id ? (
              <Tooltip title={el.mail_thread_id ? "View Conversation Thread" : "Open in email"}>
                {el.mail_thread_id ? (
                  <Button
                    size="small"
                    onClick={() =>
                      redirectToMailInbox({ appKey, threadId: el.mail_thread_id, messageId: el.mail_message_id })
                    }
                    icon={<TbInbox />}
                  ></Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() =>
                      prepareForSendEmail({
                        ncId: el._id,
                        pncId: el.reply_id,
                        appKey,
                        loanId: loanId!,
                        sendAsOrg: el.context.negotiate_as === "creditor",
                      })
                    }
                    icon={<TbSend />}
                  ></Button>
                )}
              </Tooltip>
            ) : null}
          </div>
          <Divider className="!my-2" />
        </>
      ) : null}

      <div className="relative">
        {isPrimaryEditing ? (
          <>
            {el.subject != null && (
              <div className="flex items-center gap-1 mb-2">
                <Typography.Text className="!text-sm font-semibold shrink-0">Subject:</Typography.Text>
                <Input
                  value={editedPrimarySubject}
                  onChange={(e) => setEditedPrimarySubject(e.target.value)}
                  size="small"
                  className="!text-sm flex-1"
                />
              </div>
            )}
            <Input.TextArea
              value={editedPrimaryBody}
              onChange={(e) => setEditedPrimaryBody(e.target.value)}
              autoSize={{ minRows: 4 }}
            />
            {/* <div className="flex gap-2 justify-end pt-2 mt-2 border-t border-gray-200">
              <Button
                size="small"
                disabled={sabveEditForm.isPending}
                onClick={() => {
                  setIsPrimaryEditing(false);
                  setEditedPrimarySubject(el.subject ?? "");
                  setEditedPrimaryBody(el.body ?? "");
                }}
              >
                Cancel
              </Button>
              <Button size="small" type="primary" disabled={sabveEditForm.isPending} onClick={handlePrimaryEditSave}>
                Save
              </Button>
            </div> */}
          </>
        ) : (
          <Typography.Paragraph className="!m-0" style={{ whiteSpace: "pre-wrap" }}>
            {el.body}
            <span className="block text-right float-right ml-2 mt-1 flex items-center gap-1">
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "body",
                      label: "Copy body",
                      icon: <TbCopy size={14} />,
                      onClick: () => {
                        navigator.clipboard.writeText(el.body ?? "");
                        message.success("Body copied");
                      },
                    },
                    ...(el.subject
                      ? [
                          {
                            key: "subject",
                            label: "Copy subject",
                            icon: <TbCopy size={14} />,
                            onClick: () => {
                              navigator.clipboard.writeText(el.subject ?? "");
                              message.success("Subject copied");
                            },
                          },
                          {
                            key: "all",
                            label: "Copy subject + body",
                            icon: <TbClipboard size={14} />,
                            onClick: () => {
                              navigator.clipboard.writeText(`Subject: ${el.subject}\n\n${el.body ?? ""}`);
                              message.success("Subject + body copied");
                            },
                          },
                        ]
                      : []),
                  ],
                }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<TbCopy size={14} />}
                  className="!text-gray-400 hover:!text-gray-600 !p-0.5"
                />
              </Dropdown>
              <Typography.Text className="!text-xs text-gray-500">
                {dayjs(el.timestamp).format("hh:mma MMM DD, YYYY")}
              </Typography.Text>
            </span>
          </Typography.Paragraph>
        )}

        {isGeneratedMessage ? (
          <Typography.Text
            className="absolute top-0 right-0 text-2xl"
            // copyable={{
            //   text: el.body,
            //   icon: [
            //     <TbCopy key="copy-icon" className="flex-shrink-0" />,
            //     <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
            //   ],
            //   tooltips: ["Copy", "Copied"],
            // }}
          />
        ) : null}

        {hasVersions ? (
          <VersionCarousel
            versions={el.versions}
            isLoading={isRegenerating}
            isEditing={isEditing}
            // isSaving={sabveEditForm.isPending}
            // isRegenerating={isRegenerating}
            // onEditCancel={handleEditCancel}
            appKey={appKey}
            disableMailLink={disableMailLink}
            loanId={loanId}
            editedSubject={editedVersionSubject}
            setEditedSubject={setEditedVersionSubject}
            editedBody={editedVersionBody}
            setEditedBody={setEditedVersionBody}
            // editedVersionId={editedVersionId}
            // setEditedVersionId={setEditedVersionId}
            currentVersionId={currentVersionId}
            setCurrentVersionId={setCurrentVersionId}
          />
        ) : isRegenerating ? (
          <div className="mt-3 pl-4 border-l border-gray-200">
            <div className="flex items-center justify-center p-4">
              <TbLoader2 size={36} className="animate-spin stroke-violet-600" />
            </div>
          </div>
        ) : null}
      </div>

      {isGeneratedMessage ? (
        <div className="flex gap-4 justify-between absolute -bottom-3 left-4 right-4">
          <div className="inline-flex gap-2">
            {tonalityList?.data
              ? Object.keys(tonalityList.data.result).map((tone) => (
                  <Button
                    key={tone}
                    color="primary"
                    variant="filled"
                    className="border border-solid border-violet-200"
                    loading={isRegenerating}
                    disabled={isRegenerating || isEditing}
                    onClick={() => handleTonalityRegenerate(tone)}
                  >
                    {toTitleCase(tone)}
                  </Button>
                ))
              : null}
          </div>

          <div className="inline-flex gap-2">
            {isEditing || isPrimaryEditing ? (
              <>
                <Button
                  disabled={sabveEditForm.isPending}
                  icon={<TbX />}
                  onClick={() => {
                    if (isPrimaryEditing) {
                      setIsPrimaryEditing(false);
                      setEditedPrimarySubject(el.subject ?? "");
                      setEditedPrimaryBody(el.body ?? "");
                    } else {
                      handleEditCancel();
                    }
                  }}
                >
                  Cancel
                </Button>

                <Button
                  loading={sabveEditForm.isPending}
                  disabled={sabveEditForm.isPending}
                  icon={<TbCheck />}
                  onClick={() => {
                    if (isPrimaryEditing) {
                      handlePrimaryEditSave();
                    } else if (isEditing) {
                      if (!currentVersionId) return;
                      setEditedVersionId(currentVersionId);
                      handleEditSave({
                        subject: editedVersionSubject,
                        body: editedVersionBody,
                        versionId: currentVersionId,
                      });
                    }
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  disabled={isRegenerating}
                  icon={<TbEdit />}
                  onClick={() => {
                    if (!hasVersions) {
                      setEditedPrimarySubject(el.subject ?? "");
                      setEditedPrimaryBody(el.body ?? "");
                      setIsPrimaryEditing(true);
                    } else {
                      setIsEditing(true);
                    }
                  }}
                >
                  Edit Message
                </Button>

                <Button
                  loading={isRegenerating}
                  disabled={isRegenerating}
                  onClick={() => setRegenerateMessageModalOpen(!RegenerateMessageModalOpen)}
                  icon={<TbRefresh />}
                >
                  Re-generate
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}

      {
        <InputModalRegenerate
          open={RegenerateMessageModalOpen}
          setRegenerateMessageModalOpen={setRegenerateMessageModalOpen}
          onSubmit={handleCustomRegenerate}
        />
      }
    </div>
  );
}

// function VersionCarousel({
//   versions,
//   isLoading,
//   isEditing,
//   // isSaving,
//   // isRegenerating,
//   // onEditCancel,
//   appKey,
//   editedSubject,
//   setEditedSubject,
//   editedBody,
//   setEditedBody,
//   editedVersionId,
//   // setEditedVersionId,
//   currentVersionId,
//   setCurrentVersionId,
//   loanId,
// }: {
//   versions: NegotiationChatDto[];
//   isLoading?: boolean;
//   isEditing: boolean;
//   // isSaving: boolean;
//   // isRegenerating: boolean;
//   // onEditCancel: () => void;
//   appKey?: string;
//   editedSubject: string;
//   setEditedSubject: (v: string) => void;
//   editedBody: string;
//   setEditedBody: (v: string) => void;
//   editedVersionId: string | null;
//   // setEditedVersionId: (v: string | null) => void;
//   currentVersionId: string | null;
//   setCurrentVersionId: (v: string | null) => void;
//   loanId?: string;
// }) {
//   const currentVersionIndex = versions.findIndex((v) => v._id === currentVersionId);
//   const safeIndex = currentVersionIndex !== -1 ? currentVersionIndex : versions.length - 1;
//   const currentVersion = versions[safeIndex] as Extract<NegotiationChatDto, { channel: "email" }>;

//   useEffect(() => {
//     if (!versions || versions.length === 0) return;

//     if (editedVersionId) {
//       const exists = versions.some((v) => v._id === editedVersionId);
//       if (exists) {
//         setCurrentVersionId(editedVersionId);
//         return;
//       }
//     }

//     setCurrentVersionId(versions[versions.length - 1]._id);
//   }, [versions, editedVersionId]);

//   useEffect(() => {
//     setEditedSubject(currentVersion?.subject ?? "");
//     setEditedBody(currentVersion?.body ?? "");
//   }, [currentVersion?._id]);

//   if (!versions || versions.length === 0) {
//     if (isLoading) {
//       return (
//         <div className="mt-3 pl-4 border-l border-gray-200">
//           <div className="flex items-center justify-center p-4">{loader}</div>
//         </div>
//       );
//     }
//     return null;
//   }

//   const totalVersions = versions.length;

//   const goToPrevious = () => {
//     const newIndex = safeIndex > 0 ? safeIndex - 1 : totalVersions - 1;
//     setCurrentVersionId(versions[newIndex]._id);
//   };

//   const goToNext = () => {
//     const newIndex = safeIndex < totalVersions - 1 ? safeIndex + 1 : 0;
//     setCurrentVersionId(versions[newIndex]._id);
//   };

//   return (
//     <div className="mt-3 pl-4 border-l border-gray-200">
//       <div className="relative p-2 pt-10 bg-[#f4f3f5c7] rounded-lg border border-solid border-gray-200">
//         <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
//           <div className="text-[10px] text-gray-400 uppercase tracking-wide">Regenerated • Version {safeIndex + 1}</div>

//           <div className="flex items-center gap-4">
//             {totalVersions > 1 && (
//               <div className="flex items-center gap-2">
//                 <Button
//                   type="text"
//                   size="small"
//                   icon={<TbChevronLeft className="rotate-180" />}
//                   onClick={goToPrevious}
//                   className="!p-1 !h-6 !w-6"
//                   disabled={isEditing}
//                 />
//                 <span className="text-xs text-gray-500">
//                   Version {safeIndex + 1} of {totalVersions}
//                 </span>
//                 <Button
//                   type="text"
//                   size="small"
//                   icon={<TbChevronRight />}
//                   onClick={goToNext}
//                   className="!p-1 !h-6 !w-6"
//                   disabled={isEditing}
//                 />
//               </div>
//             )}
//             <Tooltip title={currentVersion.mail_thread_id ? "View Conversation Thread" : "Open in email"}>
//               {currentVersion.mail_thread_id ? (
//                 <Button
//                   size="small"
//                   onClick={() =>
//                     redirectToMailInbox({
//                       appKey,
//                       threadId: currentVersion.mail_thread_id,
//                       messageId: currentVersion.mail_message_id,
//                     })
//                   }
//                   icon={<FiInbox />}
//                 ></Button>
//               ) : (
//                 <Button
//                   size="small"
//                   onClick={() =>
//                     prepareForSendEmail({
//                       ncId: currentVersion._id,
//                       appKey,
//                       body: currentVersion.body,
//                       subject: currentVersion.subject,
//                       loanId: loanId,
//                       sendAsOrg: currentVersion.context.negotiate_as === "creditor",
//                     })
//                   }
//                   icon={<TbSend />}
//                 ></Button>
//               )}
//             </Tooltip>
//           </div>
//         </div>

//         {isLoading ? (
//           <div className="min-h-[300px] flex items-center justify-center">{loader}</div>
//         ) : (
//           <>
//             {"subject" in currentVersion && currentVersion.subject ? (
//               isEditing ? (
//                 <div className="flex items-center gap-1 mb-1">
//                   <Typography.Text className="!text-sm font-semibold shrink-0">Subject:</Typography.Text>
//                   <Input
//                     value={editedSubject}
//                     onChange={(e) => setEditedSubject(e.target.value)}
//                     size="small"
//                     className="!text-sm flex-1"
//                   />
//                 </div>
//               ) : (
//                 <>
//                   <Typography.Title level={5} className="!mb-1">
//                     <strong>Subject: {currentVersion.subject}</strong>
//                   </Typography.Title>
//                   <Divider className="!my-2" />
//                 </>
//               )
//             ) : null}

//             <div className="relative">
//               <div className="relative">
//                 {isEditing ? (
//                   <Input.TextArea
//                     value={editedBody}
//                     onChange={(e) => setEditedBody(e.target.value)}
//                     autoSize={{ minRows: 4 }}
//                   />
//                 ) : (
//                   <>
//                     <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
//                       {currentVersion.body}
//                     </Typography.Paragraph>
//                     <div className="flex justify-end mt-1">
//                       <Dropdown
//                         trigger={["click"]}
//                         menu={{
//                           items: [
//                             {
//                               key: "body",
//                               label: "Copy body",
//                               icon: <TbCopy size={14} />,
//                               onClick: () => {
//                                 navigator.clipboard.writeText(currentVersion.body ?? "");
//                                 message.success("Body copied");
//                               },
//                             },
//                             ...("subject" in currentVersion && currentVersion.subject
//                               ? [
//                                   {
//                                     key: "subject",
//                                     label: "Copy subject",
//                                     icon: <TbCopy size={14} />,
//                                     onClick: () => {
//                                       navigator.clipboard.writeText(currentVersion.subject ?? "");
//                                       message.success("Subject copied");
//                                     },
//                                   },
//                                   {
//                                     key: "all",
//                                     label: "Copy subject + body",
//                                     icon: <TbClipboard size={14} />,
//                                     onClick: () => {
//                                       navigator.clipboard.writeText(
//                                         `Subject: ${currentVersion.subject}\n\n${currentVersion.body ?? ""}`,
//                                       );
//                                       message.success("Subject + body copied");
//                                     },
//                                   },
//                                 ]
//                               : []),
//                           ],
//                         }}
//                       >
//                         <Button
//                           type="text"
//                           size="small"
//                           icon={<TbCopy size={14} />}
//                           className="!text-gray-400 hover:!text-gray-600 !p-0.5"
//                         />
//                       </Dropdown>
//                     </div>
//                   </>
//                 )}
//               </div>
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// }

function NegotiationConfig({
  negotiationConfig,
  form,
  appKey,
  loanId,
}: Readonly<{
  form: any;
  negotiationConfig: NegotiationDto;
  appKey: string;
  loanId: string;
}>) {
  const objectiveField = Form.useWatch(["context", "objective"], form);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const selectedFlags: string[] = Form.useWatch(["context", "hardship_flags"], form) ?? [];

  const [open, setOpen] = useState(false);
  useEffect(() => {
    const negotiateAs = searchParams.get("negotiate_as");

    if (negotiateAs === "debtor" || negotiateAs === "creditor") {
      form.setFieldsValue({
        context: {
          negotiate_as: negotiateAs,
        },
      });
    }
  }, [searchParams, form]);

  const createOrUpdateConfig = ApiService.communication.usePostCommunication();
  const handleUpdateConfig = (overrides?: Partial<NegotiationDto>) => {
    if (appKey && loanId) {
      const latestValues = form.getFieldsValue(true);
      createOrUpdateConfig.mutate(
        {
          ...negotiationConfig,
          ...latestValues,
          ...overrides,
          creditor_id: loanId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["communication", "config", loanId],
            });
          },
        },
      );
    } else {
      message.error("App key or loan id not found");
    }
  };

  return (
    <div className="border-0 border-l border-solid border-gray-200 h-[calc(100vh-50px)] overflow-auto">
      <Form form={form} initialValues={negotiationConfig} layout="vertical">
        <Collapse
          expandIcon={({ isActive }) => (
            <TbChevronRight
              className={`transition-transform`}
              style={{
                transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          )}
          defaultActiveKey={["basic_info"]}
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0, borderBottom: 0 }}
          items={[
            {
              key: "basic_info",
              label: "Basic Info",
              children: (
                <>
                  <Form.Item<NegotiationDto> name={["context", "llm_input"]} label="Selected Model">
                    <Select className="w-full" defaultValue="openai" size="small">
                      <Select.Option value="openai">GPT-4o Mini</Select.Option>
                      <Select.Option value="google">Gemini 2.5 Flash</Select.Option>
                      <Select.Option value="anthropic">Claude Haiku 4.5</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item<NegotiationDto> name={["context", "objective"]} label="Objective">
                    <Select className="w-full">
                      <Select.Option value="restructure">Restructure</Select.Option>
                      <Select.Option value="settlement">Settlement</Select.Option>
                      <Select.Option value="delay">Pause</Select.Option>
                    </Select>
                  </Form.Item>
                  {/* <Form.Item<NegotiationDto> name={["context", "stage"]} label="Stage">
                    <Input />
                  </Form.Item> */}
                  <Form.Item<NegotiationDto> name={["context", "negotiate_as"]} label="Negotiate As">
                    <Select className="w-full">
                      <Select.Option value="debtor">Client</Select.Option>
                      <Select.Option value="creditor">FCDS</Select.Option>
                    </Select>
                  </Form.Item>
                  {/* <Form.Item<NegotiationDto> name={["context", "priority"]} label="Priority">
                    <Select className="w-full">
                      <Select.Option value="low">Low</Select.Option>
                      <Select.Option value="medium">Medium</Select.Option>
                      <Select.Option value="high">High</Select.Option>
                    </Select>
                  </Form.Item> */}
                  {/* <Form.Item<NegotiationDto> name={["context", "engage_legal_team"]} label="Engage Legal Team">
                    <Switch />
                  </Form.Item> */}
                  {/* <Form.Item<NegotiationDto> name={["context", "history", "round_number"]} label="Round Number">
                    <InputNumber readOnly min={1} max={6} className="w-full" />
                  </Form.Item> */}
                  <div className="grid grid-cols-2 gap-x-3">
                    <Form.Item<NegotiationDto>
                      name={["context", "history", "creditor_last_tone"]}
                      label="Creditor Last Tone"
                    >
                      <ReadOnlyText />
                    </Form.Item>
                    <Form.Item<NegotiationDto>
                      name={["context", "history", "creditor_last_offer"]}
                      label="Creditor Last Offer"
                    >
                      <ReadOnlyText />
                    </Form.Item>
                    <Form.Item<NegotiationDto> name={["context", "history", "debtor_floor"]} label="Debtor Floor">
                      <ReadOnlyText />
                    </Form.Item>
                    <Form.Item<NegotiationDto> name={["context", "history", "debtor_ceiling"]} label="Debtor Ceiling">
                      <ReadOnlyText />
                    </Form.Item>
                    <Form.Item<NegotiationDto>
                      name={["config", "strategy_config", "settlement_band", "initial_payment"]}
                      label="Initial Deposit Amount"
                      rules={objectiveField === "settlement" ? [{ required: true }] : []}
                    >
                      <InputNumber min={0} className="w-full" size="small" />
                    </Form.Item>
                    <Form.Item<NegotiationDto>
                      name={["config", "strategy_config", "settlement_band", "weekly_required"]}
                      label="Weekly Settlement?"
                      rules={objectiveField === "settlement" ? [{ required: true }] : []}
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item<NegotiationDto>
                      name={["config", "strategy_config", "restructuring_terms", "grace_period_days"]}
                      label="Grace Period days"
                    >
                      <InputNumber step={1} min={1} className="w-full" size="small" />
                    </Form.Item>
                  </div>
                </>
              ),
            },
            {
              key: "rationale_hardship",
              label: "Rationale & Hardship Flags",
              children: (
                <>
                  <Form.Item<NegotiationDto> name={["context", "rationale"]} label="Rationale">
                    <InputArray addButtonText="Add Rationale" />
                  </Form.Item>

                  <Form.Item<NegotiationDto>
                    className="!w-full"
                    name={["context", "hardship_flags"]}
                    label={
                      <div className="flex items-center justify-between !w-full">
                        <span className="mr-[130px]">Hardship Flags</span>
                        <HardshipFlagModal
                          open={open}
                          setOpen={setOpen}
                          appKey={appKey}
                          selectedFlags={selectedFlags}
                          onSelectedChange={(flags) => {
                            form.setFieldValue(["context", "hardship_flags"], flags);
                            handleUpdateConfig({
                              context: {
                                ...form.getFieldValue("context"),
                                hardship_flags: flags,
                              },
                            });
                          }}
                        />
                      </div>
                    }
                  >
                    <TagArray addButtonText="Add hardship flag" color="orange" disableInputField />
                  </Form.Item>

                  <Form.Item<NegotiationDto>
                    name={["config", "strategy_config", "anchor_strategy", "justification_bullets"]}
                    label="Justification Bullets"
                  >
                    <TagArray addButtonText="Add justification bullet" color="green" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "notes",
              label: "Notes",
              children: (
                <Form.Item<NegotiationDto> name={["context", "notes"]}>
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    placeholder="Add any additional notes here..."
                  />
                </Form.Item>
              ),
            },
          ]}
        />
      </Form>
    </div>
  );
}

function VersionCarousel({
  versions,
  isLoading,
  isEditing,
  // isRegenerating,
  // isSaving,
  // onEditCancel,
  // onEditSave,
  appKey,
  disableMailLink,
  loanId,
  editedSubject,
  setEditedSubject,
  editedBody,
  setEditedBody,
  // editedVersionId,
  // setEditedVersionId,
  currentVersionId,
  setCurrentVersionId,
}: Readonly<{
  versions: NegotiationChatDto[];
  isLoading?: boolean;
  isEditing: boolean;
  // isSaving: boolean;
  // isRegenerating: boolean;
  // onEditCancel: () => void;
  // onEditSave: (data: { subject: string; body: string; versionId: string }) => void;
  appKey?: string;
  disableMailLink: boolean;
  loanId?: string;
  editedSubject: string;
  setEditedSubject: (v: string) => void;
  editedBody: string;
  setEditedBody: (v: string) => void;
  // editedVersionId: string | null;
  // setEditedVersionId: (v: string | null) => void;
  currentVersionId: string | null;
  setCurrentVersionId: (v: string | null) => void;
}>) {
  const currentVersionIndex = versions.findIndex((v) => v._id === currentVersionId);
  const safeIndex = currentVersionIndex !== -1 ? currentVersionIndex : versions.length - 1;
  const currentVersion = versions[safeIndex] as GeneratedEmailDto;

  useEffect(() => {
    if (!versions || versions.length === 0) return;

    if (!currentVersionId) {
      setCurrentVersionId(versions[versions.length - 1]._id);
      return;
    }

    const exists = versions.some((v) => v._id === currentVersionId);

    if (!exists) {
      setCurrentVersionId(versions[versions.length - 1]._id);
    }
  }, [versions, currentVersionId]);

  useEffect(() => {
    setEditedSubject(currentVersion?.subject ?? "");
    setEditedBody(currentVersion?.body ?? "");
  }, [currentVersion?._id]);

  if (!versions || versions.length === 0) {
    if (isLoading) {
      return (
        <div className="mt-3 pl-4 border-l border-gray-200">
          <div className="flex items-center justify-center p-4">{loader}</div>
        </div>
      );
    }
    return null;
  }

  const totalVersions = versions.length;
  // const isActionsDisabled = isSaving || isRegenerating;

  const goToPrevious = () => {
    const newIndex = safeIndex > 0 ? safeIndex - 1 : totalVersions - 1;
    setCurrentVersionId(versions[newIndex]._id);
  };

  const goToNext = () => {
    const newIndex = safeIndex < totalVersions - 1 ? safeIndex + 1 : 0;
    setCurrentVersionId(versions[newIndex]._id);
  };

  return (
    <div className="mt-3 pl-4 border-l border-gray-200">
      <div className="relative p-2 pt-10 bg-[#f4f3f5c7] rounded-lg border border-solid border-gray-200">
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
          <div className="text-[10px] text-gray-400 uppercase tracking-wide">Regenerated • Version {safeIndex + 1}</div>

          <div className="flex items-center gap-4">
            {totalVersions > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  size="small"
                  icon={<TbChevronLeft className="rotate-180" />}
                  onClick={goToPrevious}
                  className="!p-1 !h-6 !w-6"
                  disabled={isEditing}
                />
                <span className="text-xs text-gray-500">
                  Version {safeIndex + 1} of {totalVersions}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<TbChevronRight />}
                  onClick={goToNext}
                  className="!p-1 !h-6 !w-6"
                  disabled={isEditing}
                />
              </div>
            )}
            {/**This openn in email button will open redirect to email tab (of our own platform ) with subject and  body as url search params */}

            {!disableMailLink || currentVersion.mail_thread_id ? (
              <Tooltip title={currentVersion.mail_thread_id ? "View Conversation Thread" : "Open in email"}>
                {currentVersion.mail_thread_id ? (
                  <Button
                    size="small"
                    onClick={() =>
                      redirectToMailInbox({
                        appKey,
                        threadId: currentVersion.mail_thread_id,
                        messageId: currentVersion.mail_message_id,
                      })
                    }
                    icon={<TbInbox />}
                  ></Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() =>
                      prepareForSendEmail({
                        ncId: currentVersion._id,
                        pncId: currentVersion.reply_id,
                        appKey,
                        loanId: loanId,
                        sendAsOrg: currentVersion.context.negotiate_as === "creditor",
                      })
                    }
                    icon={<TbSend />}
                  ></Button>
                )}
              </Tooltip>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <div className="min-h-[300px] flex items-center justify-center">{loader}</div>
        ) : (
          <>
            {"subject" in currentVersion && currentVersion.subject ? (
              isEditing ? (
                <div className="flex items-center gap-1 mb-1">
                  <Typography.Text className="!text-sm font-semibold shrink-0">Subject:</Typography.Text>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    size="small"
                    className="!text-sm flex-1"
                  />
                </div>
              ) : (
                <>
                  <Typography.Title level={5} className="!mb-1">
                    <strong>Subject: {currentVersion.subject}</strong>
                  </Typography.Title>
                  <Divider className="!my-2" />
                </>
              )
            ) : null}

            <div className="relative">
              <div className="relative">
                {isEditing ? (
                  <Input.TextArea
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    autoSize={{ minRows: 4 }}
                  />
                ) : (
                  <>
                    <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
                      {currentVersion.body}
                    </Typography.Paragraph>
                    <div className="flex justify-end mt-1">
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "body",
                              label: "Copy body",
                              icon: <TbCopy size={14} />,
                              onClick: () => {
                                navigator.clipboard.writeText(currentVersion.body ?? "");
                                message.success("Body copied");
                              },
                            },
                            ...("subject" in currentVersion && currentVersion.subject
                              ? [
                                  {
                                    key: "subject",
                                    label: "Copy subject",
                                    icon: <TbCopy size={14} />,
                                    onClick: () => {
                                      navigator.clipboard.writeText(currentVersion.subject ?? "");
                                      message.success("Subject copied");
                                    },
                                  },
                                  {
                                    key: "all",
                                    label: "Copy subject + body",
                                    icon: <TbClipboard size={14} />,
                                    onClick: () => {
                                      navigator.clipboard.writeText(
                                        `Subject: ${currentVersion.subject}\n\n${currentVersion.body ?? ""}`,
                                      );
                                      message.success("Subject + body copied");
                                    },
                                  },
                                ]
                              : []),
                          ],
                        }}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<TbCopy size={14} />}
                          className="!text-gray-400 hover:!text-gray-600 !p-0.5"
                        />
                      </Dropdown>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReadOnlyText({ value, naValue = "N/A" }: { value?: string; onChange?: () => void; naValue?: string }) {
  return <span>{value ?? naValue}</span>;
}

function InputArray({
  value,
  onChange,
  addButtonText,
}: {
  value?: string[];
  onChange?: (val: string[]) => void;
  addButtonText: string;
}) {
  const [internalValue, setInternalValue] = useState((value ?? [""]).map((el) => ({ id: nanoid(), text: el })));

  return (
    <div className="flex flex-col gap-1">
      {internalValue?.map((el, i) => {
        return (
          <Space.Compact key={el?.id}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 3 }}
              value={el?.text}
              onChange={(e) => {
                const newArr = [...internalValue];
                const baseValue = String(e.target?.value);
                const finalValue = String(baseValue ?? "")?.trim();
                newArr[i].text = baseValue;
                setInternalValue(newArr);

                const finalArr = [...newArr]?.map((el) => el?.text);
                finalArr[i] = finalValue;
                if (finalArr[i]?.length === 0) {
                  finalArr.splice(i, 1);
                }
                onChange?.(finalArr);
              }}
            />
            <Button
              size="small"
              icon={<TbX />}
              onClick={() => {
                const newArr = [...internalValue];
                newArr.splice(i, 1);
                setInternalValue(newArr?.length === 0 ? [{ id: el?.id, text: "" }] : newArr);
                onChange?.(newArr?.map((el) => el?.text));
              }}
            />
          </Space.Compact>
        );
      })}
      <Button
        type="dashed"
        size="small"
        onClick={() => {
          setInternalValue([...internalValue, { id: nanoid(), text: "" }]);
        }}
        icon={<TbPlus />}
      >
        {addButtonText}
      </Button>
    </div>
  );
}

function TagArray({
  value,
  onChange,
  addButtonText,
  disableInputField,
  color,
}: {
  value?: string[];
  onChange?: (val: string[]) => void;
  addButtonText: string;
  disableInputField?: boolean;
  color: TagProps["color"];
}) {
  const [internalValue, setInternalValue] = useState((value ?? []).map((el) => ({ id: nanoid(), text: el })));
  const [addText, setAddText] = useState("");

  useEffect(() => {
    setInternalValue((value ?? []).map((el) => ({ id: nanoid(), text: el })));
  }, [JSON.stringify(value)]);

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex flex-wrap gap-1">
        {internalValue.map((flag) => (
          <Tag
            key={flag.id}
            color={color}
            closable
            onClose={() => {
              const newArr = internalValue?.filter((el) => el.id !== flag.id);
              setInternalValue(newArr);
              onChange?.(newArr?.map((el) => el.text));
            }}
          >
            {flag.text}
          </Tag>
        ))}
      </div>
      {!disableInputField && (
        <Input.Search
          value={addText}
          onChange={(e) => setAddText(String(e.target?.value))}
          placeholder={addButtonText}
          onSearch={(val) => {
            const formattedVal = val?.trim();
            if (formattedVal?.length > 0) {
              const newArr = [...internalValue, { id: nanoid(), text: formattedVal }];
              setAddText("");
              setInternalValue(newArr);
              onChange?.(newArr?.map((el) => el.text));
            }
          }}
          enterButton="Add"
        />
      )}
    </div>
  );
}

const InputModalRegenerate = ({
  open,
  setRegenerateMessageModalOpen,
  onSubmit,
}: {
  open: boolean;
  setRegenerateMessageModalOpen: (open: boolean) => void;
  onSubmit: (value: string) => void;
}) => {
  const [value, setValue] = useState("");

  const handleGenerate = () => {
    onSubmit(value);
    setValue("");
    setRegenerateMessageModalOpen(false);
  };

  return (
    <Modal
      title="Regenerate"
      open={open}
      onCancel={() => setRegenerateMessageModalOpen(false)}
      footer={
        <Space>
          <Button onClick={() => setRegenerateMessageModalOpen(false)}>Cancel</Button>
          <Button type="primary" onClick={handleGenerate} disabled={!value.trim()}>
            Generate
          </Button>
        </Space>
      }
    >
      <Input.TextArea
        rows={3}
        placeholder="Enter text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
    </Modal>
  );
};

function HardshipFlagModal({
  open,
  setOpen,
  appKey,
  selectedFlags,
  onSelectedChange,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  appKey: string;
  selectedFlags: string[];
  onSelectedChange: (flags: string[]) => void;
}) {
  const flagshipData = ApiService.communication.useGetHardShipFlag(appKey);
  const allFlags: string[] = flagshipData.data?.result ?? [];

  console.log(allFlags, selectedFlags);

  const mutatehardShipFlag = ApiService.communication.useUpdateHardShipFlag();

  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [localAll, setLocalAll] = useState<string[]>([]);
  const [newFlag, setNewFlag] = useState("");
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (open) {
      setLocalSelected([...selectedFlags]);
      setLocalAll([...allFlags]);
      setNewFlag("");
      setAddError("");
    }
  }, [open, selectedFlags, allFlags]);

  const toggle = (flag: string) => {
    setLocalSelected((prev) => (prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag]));
  };

  const handleAddFlag = () => {
    const trimmed = newFlag.trim();
    if (!trimmed) {
      setAddError("Flag name cannot be empty.");
      return;
    }

    const isDuplicate = localAll.some((f) => f.toLowerCase() === trimmed.toLowerCase());
    if (isDuplicate) {
      setAddError(`"${trimmed}" already exists.`);
      return;
    }

    setLocalAll((prev) => [...prev, trimmed]);
    setLocalSelected((prev) => [...prev]);
    setNewFlag("");
    setAddError("");
  };

  const handleSave = () => {
    console.log(localAll, allFlags);
    const globalListChanged = JSON.stringify([...localAll].sort()) !== JSON.stringify([...allFlags].sort());

    if (globalListChanged) {
      mutatehardShipFlag.mutate({ appkey: appKey, data: localAll }, { onSuccess: () => flagshipData.refetch() });
    }

    onSelectedChange(localSelected);
    setOpen(false);
  };

  const handleDelete = (flag: string) => {
    const newAll = localAll.filter((f) => f !== flag);
    const newSelected = localSelected.filter((f) => f !== flag);

    setLocalAll(newAll);
    setLocalSelected(newSelected);

    onSelectedChange(newSelected);

    mutatehardShipFlag.mutate({ appkey: appKey, data: newAll }, { onSuccess: () => flagshipData.refetch() });
  };

  const { Search } = Input;

  const [search, setSearch] = useState("");

  const filteredFlags = localAll.filter((f) => f.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      {!open && (
        <Button size="small" onClick={() => setOpen(true)} icon={<TbEdit />} type="primary" title="Edit">
          Edit
        </Button>
      )}

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        width={460}
        title={
          <div>
            <div className="font-medium">Manage hardship flags</div>
            <div className="text-xs text-gray-500 mt-4">
              Select flags to apply. You can also add new flags here. These will be shared across all negotiation chats
              under this application.
            </div>
          </div>
        }
        footer={
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{localSelected.length} selected</span>

            <div className="flex gap-2">
              <Button size="small" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button size="small" type="primary" loading={mutatehardShipFlag.isPending} onClick={handleSave}>
                Save changes
              </Button>
            </div>
          </div>
        }
      >
        <div className="mb-4">
          <div className="text-[11px] font-medium uppercase tracking-wider text-gray-400 mb-1">Add new flag</div>

          <div className="flex gap-2">
            <Input
              size="small"
              placeholder="e.g. Medical emergency"
              value={newFlag}
              status={addError ? "error" : undefined}
              onChange={(e) => {
                setNewFlag(e.target.value);
                if (addError) setAddError("");
              }}
              onPressEnter={handleAddFlag}
            />

            <Button size="small" icon={<TbPlus />} onClick={handleAddFlag}>
              Add
            </Button>
          </div>

          {addError && <div className="text-xs text-red-500 mt-1">{addError}</div>}
        </div>

        <div className="text-[11px] font-medium uppercase tracking-wider text-gray-400 mb-2">
          All flags ({localAll.length})
        </div>

        <Search placeholder="Search flags" allowClear onChange={(e) => setSearch(e.target.value)} className="mb-2" />

        <div className="flex flex-col gap-1.5 min-h-[200px] max-h-[200px] overflow-y-auto pr-1">
          {filteredFlags.length === 0 && <div className="text-sm text-gray-400 py-2">No flags found</div>}

          {filteredFlags.map((flag) => {
            const isSelected = localSelected.includes(flag);

            return (
              <div
                key={flag}
                onClick={() => toggle(flag)}
                className={`
                group flex items-center justify-between gap-2.5 px-2.5 py-1.5 rounded-md cursor-pointer
                border transition-all select-none
                ${isSelected ? "border-blue-300 bg-blue-50" : "border-gray-200"}
              `}
              >
                {/* Left */}
                <div className="flex items-center gap-2.5">
                  <div
                    className={`
                  w-4 h-4 rounded-sm border flex items-center justify-center
                  ${isSelected ? "border-blue-500 bg-blue-500" : "border-gray-500 bg-gray-300"}
                `}
                  >
                    <TbCheck size={10} color="white" strokeWidth={3} />
                  </div>

                  <span className={`text-sm ${isSelected ? "text-blue-700" : ""}`}>{flag}</span>
                </div>

                <TbTrash
                  size={16}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(flag);
                  }}
                />
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}

const getStatusColor = (status?: Conclusion["status"]) => {
  switch (status) {
    case "accepted":
      return "green";
    case "rejected":
      return "red";
    case "ongoing":
      return "blue";
    case "stalled":
      return "orange";
    default:
      return "default";
  }
};

const getDirectionColor = (direction?: Conclusion["direction"]) => {
  switch (direction) {
    case "positive":
      return "green";
    case "negative":
      return "red";
    case "neutral":
      return "default";
    default:
      return "purple";
  }
};

const getSentimentColor = (sentiment?: Conclusion["sentiment"]) => {
  switch (sentiment) {
    case "cooperative":
      return "green";
    case "firm":
      return "blue";
    case "hostile":
      return "red";
    case "frustrated":
      return "orange";
    case "optimistic":
      return "green";
    default:
      return "default";
  }
};
const { Text, Title, Paragraph } = Typography;

const ConclusionModal = ({
  isOpen,
  onClose,
  conclusion,
  appKey,
  loanId,
}: {
  isOpen: boolean;
  onClose: () => void;
  conclusion?: Conclusion;
  appKey?: string;
  loanId?: string;
}) => {
  const generateConclusionRes = ApiService.communication.useGenerateConclusion();
  return (
    <Modal width={600} footer={null} open={isOpen} onCancel={onClose}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* Probable Conclusion */}
        <div>
          <Title level={5}>Probable Conclusion</Title>
          <Paragraph>{conclusion?.probable_conclusion || "—"}</Paragraph>
        </div>

        {/* Key Indicators */}
        <Space wrap>
          <Tag color={getStatusColor(conclusion?.status)}>Status: {conclusion?.status || "—"}</Tag>

          <Tag color={getDirectionColor(conclusion?.direction)}>Direction: {conclusion?.direction || "—"}</Tag>

          <Tag color={getSentimentColor(conclusion?.sentiment)}>Tone: {conclusion?.sentiment || "—"}</Tag>
        </Space>

        <Divider />

        {/* Last Offer */}
        <div>
          <Title level={5}>{`Debtor's`} Last Offer</Title>
          <Card size="small">
            <Text>{conclusion?.debtor_last_offer || "No offer provided"}</Text>
          </Card>
        </div>

        {/* Recommendation */}
        <div>
          <Title level={5}>Recommended Action</Title>
          <Card size="small" style={{ background: "#fafafa" }}>
            <Text>{conclusion?.recommendation || "—"}</Text>
          </Card>
        </div>
      </Space>
      <div className="flex justify-end mt-4">
        <Button
          loading={generateConclusionRes.isPending}
          onClick={() => {
            generateConclusionRes.mutate(
              { app_key: appKey, creditor_id: loanId },
              {
                onSuccess: () => {
                  notification.info({
                    message: "Conclusion regeneration in progress",
                    description: "Please wait while we generate the conclusion. This may take up to a minute.",
                  });
                  onClose();
                },
              },
            );
          }}
          variant="filled"
          color="purple"
          size="small"
          icon={<TbRefresh />}
        >
          Regenerate
        </Button>
      </div>
    </Modal>
  );
};
